import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Gift, Copy, Check, UserPlus, Share2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReferralPopover = ({ isScrolled = true }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [referralInfo, setReferralInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetchReferral = async () => {
      try {
        const res = await fetch(`${API_URL}/api/referral/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setReferralInfo(await res.json());
      } catch { /* silent */ }
    };
    fetchReferral();
  }, [isAuthenticated, token]);

  const inviteLink = referralInfo
    ? `${window.location.origin}/?ref=${referralInfo.referral_code}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = inviteLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const iconColor = isScrolled
    ? "text-stone-500 hover:text-burgundy-500"
    : "text-white/80 hover:text-gold-300";

  // Non-logged-in popover
  if (!isAuthenticated) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`relative p-2 rounded-lg transition-colors ${iconColor}`}
            data-testid="referral-icon"
          >
            <Gift className="w-5 h-5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-4 rounded-xl" data-testid="referral-popover">
          <div className="text-center">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Gift className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-sm font-bold text-stone-800">친구 초대하고 500P 받기</p>
            <p className="text-xs text-stone-400 mt-1">회원가입 후 나만의 초대링크를 받아보세요</p>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Logged-in popover
  const progressPct = referralInfo ? Math.min((referralInfo.referral_count / 10) * 100, 100) : 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`relative p-2 rounded-lg transition-colors ${iconColor}`}
          data-testid="referral-icon"
        >
          <Gift className="w-5 h-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-xl overflow-hidden" data-testid="referral-popover">
        {/* Header */}
        <div className="bg-gradient-to-r from-stone-800 to-stone-900 px-4 py-3 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold">친구 초대하기</p>
              <p className="text-xs text-stone-400 mt-0.5">1명당 <strong className="text-amber-300">500P</strong> 적립</p>
            </div>
            {referralInfo && (
              <span className="text-xs font-bold text-amber-300 bg-white/10 px-2 py-1 rounded-full">
                {referralInfo.referral_count}명 초대
              </span>
            )}
          </div>
          {/* Progress */}
          {referralInfo && (
            <div className="mt-2.5">
              <div className="flex justify-between text-[10px] text-stone-500 mb-1">
                <span>0P</span><span>5,000P</span>
              </div>
              <div className="h-1.5 bg-stone-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-[10px] text-stone-400 mt-1 text-right">
                적립: <strong className="text-amber-300">{referralInfo.total_points_earned}P</strong>
              </p>
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="px-4 py-3 border-b border-stone-100">
          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">이렇게 진행돼요</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Share2 className="w-3 h-3 text-burgundy-400 flex-shrink-0" />
              <span className="text-xs text-stone-600">초대링크를 친구에게 공유</span>
            </div>
            <div className="flex items-center gap-2">
              <UserPlus className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              <span className="text-xs text-stone-600">친구가 링크로 회원가입</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-3 h-3 text-amber-500 flex-shrink-0" />
              <span className="text-xs text-stone-600">나에게 <strong className="text-burgundy-500">500P</strong> 즉시 적립</span>
            </div>
          </div>
        </div>

        {/* Invite Link */}
        {referralInfo && (
          <div className="px-4 py-3">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">나의 초대링크</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-2 text-[11px] text-stone-500 truncate font-mono" data-testid="referral-link-display">
                {inviteLink}
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                  copied ? "bg-emerald-500 text-white" : "bg-stone-800 text-white hover:bg-stone-700"
                }`}
                data-testid="referral-copy-btn"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "복사됨" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ReferralPopover;
